import React, { useState, useEffect } from 'react';
import { postAPI, postAPIForm } from '../../../../app/api';
import editIcon from "../../../../theme/assets/images/icon/edit-icon.svg"
import deleteIcon from "../../../../theme/assets/images/icon/delete-icon.svg"
import Swal from 'sweetalert2';
import DatePickerHandler from '../../../../UIComponents/DatePickerHandler';

export default function CalendarSectionsManager() {
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        event_date: '',
        event_time: '',
        location: '',
        image: null,
        status: 1,
        is_published: 0
    });

    // Fetch all calendar events
    useEffect(() => {
        fetchCalendarEvents();
    }, []);

    const fetchCalendarEvents = async () => {
        try {
            setLoading(true);
            const response = await postAPI('calendar-events/list', {});
            if (response?.status === 1) {
                setCalendarEvents(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching calendar events:', error);
            Swal.fire('Error', 'Failed to fetch calendar events', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                image: file
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim() || !formData.description.trim() || !formData.event_date) {
            Swal.fire('Error', 'Please fill in all required fields', 'error');
            return;
        }

        try {
            setLoading(true);

            const submitData = {
                ...formData,
                id: editingId
            };

            // Upload image if a new file is selected
            if (formData.image && typeof formData.image !== 'string') {
                const imgFormData = new FormData();
                imgFormData.append('baseDir', 'calendar-events');
                imgFormData.append('filename', 'event_image');
                imgFormData.append('original_file', formData.image);

                const imgResponse = await postAPIForm('file/uploadFile', imgFormData);
                if (imgResponse?.status === 1) {
                    submitData.image = imgResponse?.filepath;
                } else {
                    console.error('Image upload failed:', imgResponse);
                }
            } else if (typeof formData.image === 'string') {
                submitData.image = formData.image;
            }

            const response = await postAPI('calendar-events/create', submitData);

            if (response?.status === 1) {
                Swal.fire('Success', `Calendar event ${editingId ? 'updated' : 'created'} successfully`, 'success');
                fetchCalendarEvents();
                resetForm();
            } else {
                throw new Error(response?.message || 'Failed to save calendar event');
            }
        } catch (error) {
            console.error('Error saving calendar event:', error);
            Swal.fire('Error', error.message || 'Failed to save calendar event', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (event) => {
        setFormData({
            title: event.title || '',
            description: event.description || '',
            event_date: event.event_date || '',
            event_time: event.event_time || '',
            location: event.location || '',
            image: event.image || null,
            status: event.status || 1,
            is_published: event.is_published || 0
        });
        setEditingId(event.id);
        setShowForm(true);
    };

    const handleDelete = async (eventId) => {
        try {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: 'You won\'t be able to revert this!',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, delete it!'
            });

            if (result.isConfirmed) {
                setLoading(true);
                const response = await postAPI('calendar-events/delete', { id: eventId });

                if (response?.status === 1) {
                    Swal.fire('Deleted!', 'Calendar event has been deleted.', 'success');
                    fetchCalendarEvents();
                } else {
                    throw new Error(response?.message || 'Failed to delete calendar event');
                }
            }
        } catch (error) {
            console.error('Error deleting calendar event:', error);
            Swal.fire('Error', error.message || 'Failed to delete calendar event', 'error');
        } finally {
            setLoading(false);
        }
    };

    const togglePublishStatus = async (eventId, currentStatus) => {
        try {
            setLoading(true);
            const response = await postAPI('calendar-events/toggle-publish', {
                id: eventId,
                is_published: currentStatus === 1 ? 0 : 1
            });

            if (response?.status === 1) {
                Swal.fire('Success', `Event ${currentStatus === 1 ? 'unpublished' : 'published'} successfully`, 'success');
                fetchCalendarEvents();
            } else {
                throw new Error(response?.message || 'Failed to update publish status');
            }
        } catch (error) {
            console.error('Error updating publish status:', error);
            Swal.fire('Error', error.message || 'Failed to update publish status', 'error');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            event_date: '',
            event_time: '',
            location: '',
            image: null,
            status: 1,
            is_published: 0
        });
        setShowForm(false);
        setEditingId(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB');
    };

    return (
        <div className="container-fluid py-4">
            <div className="row">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h3 className="mb-0">Calendar Events Management</h3>
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowForm(!showForm)}
                            disabled={loading}
                        >
                            {showForm ? 'Cancel' : 'Add New Event'}
                        </button>
                    </div>

                    {showForm && (
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="mb-0">{editingId ? 'Edit Calendar Event' : 'Add New Calendar Event'}</h5>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Event Title *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter event title"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Event Date *</label>
                                            <DatePickerHandler
                                                form_data={formData}
                                                setFormData={setFormData}
                                                Name="event_date"
                                                Label=""
                                                is_disabled={loading}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Event Time</label>
                                            <input
                                                type="time"
                                                className="form-control"
                                                name="event_time"
                                                value={formData.event_time}
                                                onChange={handleChange}
                                                placeholder="Select event time"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Location</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="location"
                                                value={formData.location}
                                                onChange={handleChange}
                                                placeholder="Enter event location"
                                            />
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label">Event Description *</label>
                                            <textarea
                                                className="form-control"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                rows="4"
                                                required
                                                placeholder="Enter detailed event description"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Event Image</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                            />
                                            {formData.image && (
                                                <div className="mt-2">
                                                    {typeof formData.image === 'string' ? (
                                                        <img src={formData.image} alt="Event" style={{ maxHeight: '100px', borderRadius: '4px' }} />
                                                    ) : (
                                                        <small className="text-success">New image selected: {formData.image.name}</small>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="is_published"
                                                    checked={formData.is_published === 1}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label">
                                                    Publish this event
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button
                                            type="submit"
                                            className="btn btn-success"
                                            disabled={loading}
                                        >
                                            {loading ? 'Saving...' : (editingId ? 'Update Event' : 'Create Event')}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={resetForm}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Events List Table */}
                    <div className="card">
                        <div className="card-body">
                            <div className="card-header">
                                <h5 className="mb-0">All Calendar Events</h5>
                            </div>
                            {loading && !showForm ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : calendarEvents.length === 0 ? (
                                <div className="text-center py-4">
                                    <h5 className="text-muted">No calendar events found</h5>
                                    <p className="text-muted">Click "Add New Event" to create your first calendar event.</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-striped cus-table">
                                        <thead className="table-dark">
                                            <tr>
                                                <th>Title</th>
                                                <th>Date</th>
                                                <th>Time</th>
                                                <th>Location</th>
                                                <th>Status</th>
                                                <th>Published</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {calendarEvents.map((event) => (
                                                <tr key={event.id}>
                                                    <td>
                                                        <strong>{event.title}</strong>
                                                        <br />
                                                        <small className="text-muted">{event.description?.substring(0, 50)}...</small>
                                                    </td>
                                                    <td>{formatDate(event.event_date)}</td>
                                                    <td>{event.event_time || '-'}</td>
                                                    <td>{event.location || '-'}</td>
                                                    <td>
                                                        <span className={`badge ${event.status === 1 ? 'bg-success' : 'bg-danger'}`}>
                                                            {event.status === 1 ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className={`btn btn-sm ${event.is_published === 1 ? 'btn-success' : 'btn-warning'}`}
                                                            onClick={() => togglePublishStatus(event.id, event.is_published)}
                                                            disabled={loading}
                                                        >
                                                            {event.is_published === 1 ? 'Published' : 'Draft'}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                className="btn btn-sm"
                                                                onClick={() => handleEdit(event)}
                                                                disabled={loading}
                                                                title="Edit Event"
                                                            >
                                                                <img src={editIcon} alt="Edit" width="16" height="16" />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm"
                                                                onClick={() => handleDelete(event.id)}
                                                                disabled={loading}
                                                                title="Delete Event"
                                                            >
                                                                <img src={deleteIcon} alt="Delete" width="16" height="16" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div >
        </div >
    );
}